import React from "react";
import { api, apiUrl } from "../../api/api";
import Modal from "../components/Modal";

function Input({ label, ...props }) {
  return (
    <div>
      <label className="text-white/70 text-sm">{label}</label>
      <input
        {...props}
        className="mt-2 w-full rounded-2xl bg-black/30 border border-white/10 px-4 py-3 text-white placeholder-white/30
        focus:outline-none focus:ring-2 focus:ring-blue-500/60"
      />
    </div>
  );
}

function Textarea({ label, ...props }) {
  return (
    <div>
      <label className="text-white/70 text-sm">{label}</label>
      <textarea
        {...props}
        className="mt-2 w-full min-h-[110px] rounded-2xl bg-black/30 border border-white/10 px-4 py-3 text-white placeholder-white/30
        focus:outline-none focus:ring-2 focus:ring-blue-500/60"
      />
    </div>
  );
}

export default function Items() {
  const [search, setSearch] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState("create"); // create | edit
  const [busy, setBusy] = React.useState(false);

  const [form, setForm] = React.useState({
    id: null,
    name: "",
    barcode: "",
    quantity: 0,
    unit_cost: "",
    profit_margin: 15,
    description: "",
    image_url: "",
  });

  async function load(q = "") {
    setLoading(true);
    setError("");
    try {
      const data = await api.listItems(q);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Failed to load items");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSearch(e) {
    e.preventDefault();
    load(search.trim());
  }

  function openCreate() {
    setMode("create");
    setForm({
      id: null,
      name: "",
      barcode: "",
      quantity: 0,
      unit_cost: "",
      profit_margin: 15,
      description: "",
      image_url: "",
    });
    setOpen(true);
  }

  function openEdit(it) {
    setMode("edit");
    setForm({
      id: it.id,
      name: it.name || "",
      barcode: it.barcode || "",
      quantity: it.quantity ?? 0,
      unit_cost: it.unit_cost ?? "",
      profit_margin: it.profit_margin ?? 15,
      description: it.description || "",
      image_url: it.image_url || "",
    });
    setOpen(true);
  }

  async function uploadImage(file) {
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.uploadImage(file);
      // backend returns "/uploads/....jpg"
      setForm((f) => ({ ...f, image_url: res.path }));
    } catch (e) {
      alert(e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    setError("");
    try {
      const payload = {
        name: form.name,
        barcode: form.barcode || null,
        quantity: Number(form.quantity) || 0,
        unit_cost: Number(form.unit_cost) || 0,
        profit_margin: Number(form.profit_margin) || 15,
        description: form.description || null,
        image_url: form.image_url || null,
      };

      if (mode === "create") {
        await api.createItem(payload);
      } else {
        await api.updateItem(form.id, payload);
      }

      setOpen(false);
      await load(search.trim());
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.deleteItem(id);
      await load(search.trim());
    } catch (e) {
      alert(e.message || "Delete failed");
    }
  }

  const fullImageUrl = form.image_url ? apiUrl(form.image_url) : "";

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
          <form onSubmit={onSearch} className="flex-1 flex gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-2xl bg-black/30 border border-white/10 px-4 py-3 text-white placeholder-white/30
              focus:outline-none focus:ring-2 focus:ring-blue-500/60"
              placeholder="Search by name or barcode..."
            />
            <button className="rounded-2xl px-5 py-3 bg-white/10 border border-white/10 text-white hover:bg-white/15 transition">
              Search
            </button>
          </form>

          <button
            onClick={openCreate}
            className="rounded-2xl px-5 py-3 bg-gradient-to-r from-blue-500 to-violet-500 text-white font-semibold hover:opacity-95 transition"
          >
            + Add Item
          </button>
        </div>

        {error ? (
          <div className="mt-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-200 text-sm">
            {error}
          </div>
        ) : null}
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <div className="text-white font-semibold">Inventory Items</div>
          <div className="text-white/60 text-sm">Create, edit, delete, upload images, add description</div>
        </div>

        <div className="p-4 overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-white/60 text-sm">
                <th className="py-3 px-3">Item</th>
                <th className="py-3 px-3">Barcode</th>
                <th className="py-3 px-3">Qty</th>
                <th className="py-3 px-3">Unit Cost</th>
                <th className="py-3 px-3">Sell</th>
                <th className="py-3 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-8 px-3 text-white/60" colSpan={6}>
                    Loading…
                  </td>
                </tr>
              ) : null}

              {!loading && items.map((it) => (
                <tr key={it.id} className="border-t border-white/10 text-white/90 align-top">
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-2xl bg-black/30 border border-white/10 overflow-hidden grid place-items-center">
                        {it.image_url ? (
                          <img
                            src={apiUrl(it.image_url)}
                            alt={it.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-white/40">📦</span>
                        )}
                      </div>
                      <div>
                        <div className="font-semibold">{it.name}</div>
                        {it.description ? (
                          <div className="text-white/60 text-sm line-clamp-1 max-w-[420px]">
                            {it.description}
                          </div>
                        ) : (
                          <div className="text-white/40 text-sm">No description</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-white/70">{it.barcode || "-"}</td>
                  <td className="py-3 px-3">{it.quantity ?? 0}</td>
                  <td className="py-3 px-3">${Number(it.unit_cost || 0).toFixed(2)}</td>
                  <td className="py-3 px-3">${Number(it.sell_price || 0).toFixed(2)}</td>
                  <td className="py-3 px-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => openEdit(it)}
                        className="rounded-xl px-3 py-2 bg-white/10 border border-white/10 text-white hover:bg-white/15 transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => remove(it.id)}
                        className="rounded-xl px-3 py-2 bg-red-500/15 border border-red-500/30 text-red-200 hover:bg-red-500/20 transition"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!loading && items.length === 0 ? (
                <tr>
                  <td className="py-10 px-3 text-white/60" colSpan={6}>
                    No items found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={open}
        title={mode === "create" ? "Add Item" : "Edit Item"}
        onClose={() => !busy && setOpen(false)}
      >
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Name *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Lenovo X1 Carbon"
          />
          <Input
            label="Barcode"
            value={form.barcode}
            onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))}
            placeholder="optional"
          />
          <Input
            label="Quantity"
            type="number"
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
          />
          <Input
            label="Unit Cost"
            type="number"
            step="0.01"
            value={form.unit_cost}
            onChange={(e) => setForm((f) => ({ ...f, unit_cost: e.target.value }))}
          />

          <Input
            label="Profit Margin (%)"
            type="number"
            step="0.01"
            value={form.profit_margin}
            onChange={(e) => setForm((f) => ({ ...f, profit_margin: e.target.value }))}
          />

          <div>
            <label className="text-white/70 text-sm">Image</label>
            <div className="mt-2 flex items-center gap-3">
              <div className="h-14 w-14 rounded-2xl bg-black/30 border border-white/10 overflow-hidden grid place-items-center">
                {fullImageUrl ? (
                  <img src={fullImageUrl} alt="preview" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-white/40">🖼️</span>
                )}
              </div>

              <label className="cursor-pointer rounded-2xl px-4 py-3 bg-white/10 border border-white/10 text-white hover:bg-white/15 transition">
                {busy ? "Uploading..." : "Upload Image"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadImage(e.target.files?.[0])}
                  disabled={busy}
                />
              </label>
            </div>

            {form.image_url ? (
              <div className="mt-2 text-white/60 text-xs break-all">
                {form.image_url}
              </div>
            ) : null}
          </div>

          <div className="md:col-span-2">
            <Textarea
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Specs, notes, condition, etc..."
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={() => setOpen(false)}
            disabled={busy}
            className="rounded-2xl px-5 py-3 bg-white/10 border border-white/10 text-white hover:bg-white/15 transition disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !form.name}
            className="rounded-2xl px-5 py-3 bg-gradient-to-r from-blue-500 to-violet-500 text-white font-semibold hover:opacity-95 transition disabled:opacity-60"
          >
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
